import React, { useEffect, useState, useRef } from 'react';
import { Image as ImageIcon, Save, RefreshCw, Loader2, Eye } from 'lucide-react';
import aboutService from '../../services/aboutService';
import ImageGallery from '../../components/ImageGallery';
import '../../styles/admin/AboutPage.css';

const API_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000');

const defaultContent = {
  hero_image: '/assets/aboutp.jpeg',
  role: 'Certified Fitness Coach',
  title_line1: "Hey I'm",
  title_highlight: 'Daniella',
  tagline: "I'm here to prove that fitness doesn't have to feel intimidating or impossible.",
  intro: "Like many people, my journey into fitness wasn't perfect or predictable. But along the way, I discovered something powerful: when you find an approach that works for you, fitness becomes something you actually enjoy.",
  story_title: 'About',
  story_body: "What truly defines my coaching style is the empathy and care I bring to understanding each client's needs. This allows us to build a plan that works for your body, your routine, and your lifestyle.<br/><br/>My programs follow a personalised approach that is effective, realistic, and sustainable. Each one is thoughtfully designed to fit into your life, helping you move forward with clarity, confidence, and consistency.",
  philosophy_title: 'My Philosophy',
  philosophy_body: "Fitness should feel empowering, not exhausting.<br/><br/>I believe the best results come from building sustainable habits and learning to enjoy the process. There's no \"perfect\" routine -- only the one that works for your body and your life.<br/><br/>My goal is to support you every step of the way -- celebrating your progress, helping you overcome challenges, and reminding you of how far you've come.<br/><br/>Because when fitness becomes part of your lifestyle rather than a short-term goal, the results last.",
  cta_title: "Let's make your goals a reality!",
  cta_text: "Ready to start your fitness journey? I'm here to make fitness fun, simple and sustainable for YOU!",
  cta_button_text: 'All Programs',
  cta_button_link: '/programs'
};

const RichTextEditor = ({ label, value, onChange, placeholder }) => {
  const editorRef = useRef(null);

  const handleInput = (e) => {
    onChange(e.currentTarget.innerHTML);
  };

  const applyCommand = (command) => {
    document.execCommand(command, false, null);
    editorRef.current?.focus();
  };

  const insertLink = () => {
    const url = prompt('Enter URL');
    if (url) {
      document.execCommand('createLink', false, url);
      editorRef.current?.focus();
    }
  };

  return (
    <div className="rich-editor">
      <div className="editor-label">{label}</div>
      <div className="editor-toolbar">
        <button type="button" onClick={() => applyCommand('bold')}>B</button>
        <button type="button" onClick={() => applyCommand('italic')}><i>I</i></button>
        <button type="button" onClick={() => applyCommand('underline')}><u>U</u></button>
        <button type="button" onClick={() => applyCommand('insertUnorderedList')}>• List</button>
        <button type="button" onClick={insertLink}>Link</button>
      </div>
      <div
        className="editor-surface"
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        dangerouslySetInnerHTML={{ __html: value || '' }}
        placeholder={placeholder}
      />
    </div>
  );
};

export default function AdminAbout() {
  const [form, setForm] = useState(defaultContent);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [status, setStatus] = useState(null);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const data = await aboutService.getAdminAbout();
      setForm(data || defaultContent);
    } catch (error) {
      console.error('Failed to load about page', error);
      setStatus({ type: 'error', message: 'Could not load current content' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const data = await aboutService.updateAbout(form);
      setForm(data || form);
      setStatus({ type: 'success', message: 'About page updated' });
    } catch (error) {
      console.error('Save failed', error);
      setStatus({ type: 'error', message: 'Save failed. Please try again.' });
    } finally {
      setSaving(false);
      setTimeout(() => setStatus(null), 3000);
    }
  };

  const resetToDefault = () => {
    setForm(defaultContent);
    setStatus({ type: 'info', message: 'Reset to defaults (not saved yet)' });
  };

  const getImageUrl = (path) => {
    if (!path) return '/assets/aboutp.jpeg';
    if (path.startsWith('http')) return path;
    return `${API_URL}${path}`;
  };

  return (
    <div className="admin-about">
      <div className="about-header">
        <div>
          <p className="eyebrow">About Page</p>
          <h1>Hero, story & CTA</h1>
          <p className="muted">Update the live About page. Text areas support bold, italics, links and bullet lists.</p>
        </div>
        <div className="header-actions">
          <a className="ghost-btn" href="/about" target="_blank" rel="noreferrer">
            <Eye size={16} /> View live
          </a>
          <button className="ghost-btn" onClick={resetToDefault}>
            <RefreshCw size={16} /> Reset
          </button>
          <button className="primary-btn" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {status && (
        <div className={`status-banner ${status.type}`}>
          {status.message}
        </div>
      )}

      {loading ? (
        <div className="about-card loading">
          <Loader2 className="spin" size={28} />
          <p>Loading content...</p>
        </div>
      ) : (
        <>
          <div className="about-card">
            <div className="card-title">Hero</div>
            <div className="hero-grid">
              <div className="image-picker">
                <div className="image-label">Hero image</div>
                <div className="image-preview">
                  <img src={getImageUrl(form.hero_image)} alt="About hero" />
                </div>
                <div className="image-actions">
                  <button className="ghost-btn" onClick={() => setGalleryOpen(true)}>
                    <ImageIcon size={16} /> Upload / choose
                  </button>
                  <small>Recommended: tall portrait, min 1200px, PNG/JPEG.</small>
                </div>
              </div>
              <div className="hero-fields">
                <label className="field">
                  <span>Role badge</span>
                  <input
                    type="text"
                    value={form.role}
                    onChange={(e) => handleChange('role', e.target.value)}
                    placeholder="Certified Fitness Coach"
                  />
                </label>
                <div className="two-col">
                  <label className="field">
                    <span>Title line</span>
                    <input
                      type="text"
                      value={form.title_line1}
                      onChange={(e) => handleChange('title_line1', e.target.value)}
                    />
                  </label>
                  <label className="field">
                    <span>Highlighted name</span>
                    <input
                      type="text"
                      value={form.title_highlight}
                      onChange={(e) => handleChange('title_highlight', e.target.value)}
                    />
                  </label>
                </div>
                <RichTextEditor
                  label="Tagline"
                  value={form.tagline}
                  onChange={(html) => handleChange('tagline', html)}
                  placeholder="Short line under the title"
                />
                <RichTextEditor
                  label="Intro"
                  value={form.intro}
                  onChange={(html) => handleChange('intro', html)}
                  placeholder="Longer intro paragraph"
                />
              </div>
            </div>
          </div>

          <div className="about-card">
            <div className="card-title">Story & Philosophy</div>
            <div className="two-col">
              <div className="stack">
                <label className="field">
                  <span>Story heading</span>
                  <input
                    type="text"
                    value={form.story_title}
                    onChange={(e) => handleChange('story_title', e.target.value)}
                  />
                </label>
                <RichTextEditor
                  label="Story body"
                  value={form.story_body}
                  onChange={(html) => handleChange('story_body', html)}
                />
              </div>
              <div className="stack">
                <label className="field">
                  <span>Philosophy heading</span>
                  <input
                    type="text"
                    value={form.philosophy_title}
                    onChange={(e) => handleChange('philosophy_title', e.target.value)}
                  />
                </label>
                <RichTextEditor
                  label="Philosophy body"
                  value={form.philosophy_body}
                  onChange={(html) => handleChange('philosophy_body', html)}
                />
              </div>
            </div>
          </div>

          <div className="about-card">
            <div className="card-title">CTA</div>
            <div className="two-col">
              <label className="field">
                <span>CTA heading</span>
                <input
                  type="text"
                  value={form.cta_title}
                  onChange={(e) => handleChange('cta_title', e.target.value)}
                />
              </label>
              <label className="field">
                <span>Button label</span>
                <input
                  type="text"
                  value={form.cta_button_text}
                  onChange={(e) => handleChange('cta_button_text', e.target.value)}
                />
              </label>
            </div>
            <div className="two-col">
              <RichTextEditor
                label="CTA copy"
                value={form.cta_text}
                onChange={(html) => handleChange('cta_text', html)}
              />
              <label className="field">
                <span>Button link</span>
                <input
                  type="text"
                  value={form.cta_button_link}
                  onChange={(e) => handleChange('cta_button_link', e.target.value)}
                  placeholder="/programs"
                />
              </label>
            </div>
          </div>

          <div className="footer-actions">
            <button className="ghost-btn" onClick={resetToDefault}>
              <RefreshCw size={16} /> Reset (not saved)
            </button>
            <button className="primary-btn" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="spin" size={16} /> : <Save size={16} />}
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </>
      )}

      <ImageGallery
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        onSelect={(url) => {
          setForm((prev) => ({ ...prev, hero_image: url }));
          setGalleryOpen(false);
        }}
        currentImage={form.hero_image}
      />
    </div>
  );
}
